import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SlackLogoPrimary from '../../../../../assets/slack-logo-primary.svg';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import {
  getSlackChannelsPage,
  UserKeys,
  SlackChannel,
} from '../../../../../api/users-api';
import { pagesConfig } from '../../../../../shared/pages-config';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import type { GridColDef } from '@mui/x-data-grid-pro';
import ManageSlackChannelsGridToolbar from './manage-slack-channels-grid-toolbar';
import SimpleBreadcrumbsPage from '../../../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { useToolbar } from '../../../../../shared/shared-data-grid-toolbar-helper';
import CreateSlackCollectionDialog from './create-slack-collection-dialog';
import SlackCollectionDetailsDrawer, {
  SlackCollection,
} from './slack-collection-details-drawer';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Icon from '@mui/material/Icon';

const MOCK_SLACK_COLLECTIONS: SlackCollection[] = [
  {
    id: 'collection-product-team',
    name: 'Slack - Product Team',
    description: 'Add new user to Slack channels',
    channels: [
      { id: 'C-BLACK-POD', name: 'Black Pod' },
      { id: 'C-GPANEL-TEAM', name: 'gPanel Team' },
      { id: 'C-PRODUCT-TEAM', name: 'Product Team' },
      { id: 'C-RED-POD', name: 'Red Pod' },
    ],
    createTime: '2026-09-30T12:23:00',
    updateTime: '2026-09-30T12:23:00',
  },
  {
    id: 'collection-sales-marketing',
    name: 'Slack - Sales and Marketing',
    description: 'Sales and marketing team channels',
    channels: [
      { id: 'C-SALES', name: 'Sales' },
      { id: 'C-MARKETING', name: 'Marketing' },
    ],
    createTime: '2026-07-21T12:23:00',
    updateTime: '2026-07-21T12:23:00',
  },
  {
    id: 'collection-promevo-team',
    name: 'Promevo Team',
    description: 'Company-wide team channels',
    channels: [
      { id: 'C-GENERAL', name: 'general' },
      { id: 'C-ANNOUNCEMENTS', name: 'announcements' },
    ],
    createTime: '2026-07-21T12:23:00',
    updateTime: '2026-07-21T12:23:00',
  },
  {
    id: 'collection-weekly-townhall',
    name: 'Weekly Townhall',
    description: 'Channels for weekly town hall updates',
    channels: [{ id: 'C-TOWNHALL', name: 'town-hall' }],
    createTime: '2026-07-21T12:23:00',
    updateTime: '2026-07-21T12:23:00',
  },
  {
    id: 'collection-dev-team',
    name: 'Dev Team',
    description: 'Engineering team channels',
    channels: [
      { id: 'C-DEV', name: 'dev' },
      { id: 'C-RELEASES', name: 'releases' },
    ],
    createTime: '2026-07-21T12:23:00',
    updateTime: '2026-07-21T12:23:00',
  },
];

function formatCollectionDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString('en-US');
}

export default function ManageSlackChannelsPage() {
  const [tabValue, setTabValue] = useState(0);

  // Selections & Collections State
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [collections, setCollections] = useState<SlackCollection[]>(
    MOCK_SLACK_COLLECTIONS
  );
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] =
    useState<SlackCollection | null>(null);
  const [editingCollection, setEditingCollection] =
    useState<SlackCollection | null>(null);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [UserKeys.SlackChannels],
      queryFn: ({ pageParam = '' }) => getSlackChannelsPage(pageParam),
      initialPageParam: '',
      getNextPageParam: (lastPage) =>
        lastPage.response_metadata?.next_cursor || undefined,
    });

  const channels = useMemo(() => {
    return data?.pages.flatMap((page) => page.channels || []) || [];
  }, [data]);

  const columns: GridColDef<SlackChannel>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Slack Channels',
        flex: 1,
      },
      {
        field: 'private',
        headerName: 'Channel Type',
        flex: 1,
        valueGetter: (_value: boolean | undefined, row: SlackChannel) =>
          row.private || row.is_private || row.isPrivate ? 'Private' : 'Public',
      },
      {
        field: 'numOfMembers',
        headerName: 'Number of Members',
        flex: 1,
      },
    ],
    []
  );

  const handleUpdateCollection = (updatedCollection: SlackCollection) => {
    setCollections((prev) =>
      prev.map((col) =>
        col.id === updatedCollection.id ? updatedCollection : col
      )
    );
    if (selectedCollection?.id === updatedCollection.id) {
      setSelectedCollection(updatedCollection);
    }
    setEditingCollection(null);
  };

  const handleDeleteCollection = (collectionId: string) => {
    setCollections((prev) => prev.filter((col) => col.id !== collectionId));
    if (selectedCollection?.id === collectionId) {
      setSelectedCollection(null);
    }
  };

  const collectionColumns: GridColDef<SlackCollection>[] = [
    {
      field: 'name',
      headerName: 'Collection Name',
      flex: 1,
    },
    {
      field: 'createTime',
      headerName: 'Date Created',
      width: 220,
      renderCell: ({ row }) => formatCollectionDate(row.createTime),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="edit-collection"
          icon={<EditOutlinedIcon />}
          label="Edit Collection"
          showInMenu
          onClick={() => setEditingCollection(row)}
        />,
        <GridActionsCellItem
          key="delete-collection"
          icon={<DeleteOutlineIcon />}
          label="Delete Collection"
          showInMenu
          onClick={() => handleDeleteCollection(row.id)}
        />,
      ],
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateCollection = (newCollection: SlackCollection) => {
    setCollections((prev) => [newCollection, ...prev]);
    setSelectedChannelIds([]); // Clear selection upon creation
  };

  const toolbarProps = useMemo(
    () => ({
      openCreateDialog: () => setCreateModalOpen(true),
    }),
    []
  );

  const slackToolbar = useToolbar(ManageSlackChannelsGridToolbar, toolbarProps);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.administration.name },
        { label: pagesConfig.settings.name },
        {
          label: pagesConfig.integrations.name,
          path: `${pagesConfig.settings.link}/${pagesConfig.integrations.link}`,
        },
        { label: 'Slack' },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          height: '100%',
        }}
      >
        <Card
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 400,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="slack channels tabs"
            >
              <Tab
                label="All Slack Channels"
                icon={
                  <Icon
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                    }}
                  >
                    <Box
                      component="img"
                      src={SlackLogoPrimary}
                      alt="Slack Logo"
                      sx={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Icon>
                }
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: tabValue === 0 ? 600 : 400,
                  minHeight: '48px',
                }}
              />
              <Tab
                label="Collections"
                icon={<AutoAwesomeMotionOutlinedIcon />}
                iconPosition="start"
                sx={{
                  textTransform: 'none',
                  fontWeight: tabValue === 1 ? 600 : 400,
                  minHeight: '48px',
                }}
              />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Box sx={{ flex: 1 }}>
              <SharedDataGridClient
                slots={{
                  toolbar: slackToolbar,
                }}
                rows={channels}
                columns={columns}
                loading={isLoading || isFetchingNextPage}
                pagination
                pageSizeOptions={[25, 50, 100]}
                checkboxSelection
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                onPaginationModelChange={(model) => {
                  if (
                    hasNextPage &&
                    !isFetchingNextPage &&
                    channels.length > 0 &&
                    model.page * model.pageSize >=
                      channels.length - model.pageSize
                  ) {
                    fetchNextPage();
                  }
                }}
                selectedRows={selectedChannelIds}
                setSelectedRows={setSelectedChannelIds}
                keepNonExistentRowsSelected
                sx={{ border: 'none', borderTopRadius: 0 }}
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ flex: 1 }}>
              <SharedDataGridClient
                slots={{ toolbar: slackToolbar }}
                rows={collections}
                columns={collectionColumns}
                getRowId={(row) => row.id}
                onRowClick={(params) => setSelectedCollection(params.row)}
                pagination
                pageSizeOptions={[10, 25, 50]}
                rowHeight={44}
                sx={{
                  height: '100%',
                  border: 'none',
                  borderTopRadius: 0,
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                }}
              />
            </Box>
          )}
        </Card>
      </Box>

      <SlackCollectionDetailsDrawer
        collection={selectedCollection}
        onClose={() => setSelectedCollection(null)}
        onEdit={(col) => setEditingCollection(col)}
        onDelete={(col) => handleDeleteCollection(col.id)}
      />

      {isCreateModalOpen && (
        <CreateSlackCollectionDialog
          open={isCreateModalOpen}
          handleClose={() => setCreateModalOpen(false)}
          selectedChannelIds={selectedChannelIds}
          allChannels={channels}
          onCreateCollection={handleCreateCollection}
        />
      )}

      {editingCollection && (
        <CreateSlackCollectionDialog
          open={Boolean(editingCollection)}
          handleClose={() => setEditingCollection(null)}
          collectionToEdit={editingCollection}
          allChannels={channels}
          onUpdateCollection={handleUpdateCollection}
        />
      )}
    </SimpleBreadcrumbsPage>
  );
}
